import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// «Добавить ссылку» к выделенному тексту в строке ввода. Ссылка хранится в
// черновике markdown-ом [текст](url) — его уже понимает parseMarkdown
// (@tg/shared): при отправке URL уезжает в entity "link", а у получателя
// виден только текст, клик открывает адрес. До отправки ссылку можно
// изменить или удалить тем же меню (ПКМ / Ctrl+K на ссылке).

// Тот же шаблон, что в packages/shared/src/markdown.ts — иначе вставленная
// здесь ссылка могла бы не распознаться при отправке.
const MARKDOWN_LINK = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g;

export interface MarkdownLinkRange {
  start: number;
  end: number;
  label: string;
  url: string;
}

/** Ссылка [текст](url), внутри которой (или на которой) стоит выделение. */
export const findMarkdownLinkAt = (
  text: string,
  selectionStart: number,
  selectionEnd: number
): MarkdownLinkRange | null => {
  MARKDOWN_LINK.lastIndex = 0;
  for (let match = MARKDOWN_LINK.exec(text); match; match = MARKDOWN_LINK.exec(text)) {
    const start = match.index;
    const end = start + match[0].length;
    if (selectionStart >= start && selectionEnd <= end) {
      return { start, end, label: match[1] ?? "", url: match[2] ?? "" };
    }
  }
  return null;
};

/** http(s)-адрес или null. Без схемы дописываем https:// (как в TG). */
export const normalizeLinkUrl = (raw: string): string | null => {
  const value = raw.trim();
  if (value.length === 0) {
    return null;
  }
  const withScheme = /^https?:\/\//i.test(value) ? value : `https://${value}`;
  try {
    const parsed = new URL(withScheme);
    if ((parsed.protocol !== "http:" && parsed.protocol !== "https:") || !parsed.hostname.includes(".")) {
      return null;
    }
  } catch {
    return null;
  }
  // В markdown-шаблоне URL не может содержать пробелы и «)».
  return withScheme.replace(/\s/g, "%20").replace(/\)/g, "%29");
};

/** Текст ссылки: квадратные скобки и переносы сломали бы разбор markdown. */
export const cleanLinkLabel = (label: string): string =>
  label.replace(/[[\]]/g, "").replace(/\s*\n\s*/g, " ").trim();

/** Markdown-ссылка [текст](url). */
export const buildMarkdownLink = (label: string, url: string): string =>
  `[${cleanLinkLabel(label)}](${url})`;

interface ComposerLinkModalProps {
  initialLabel: string;
  initialUrl: string;
  isExisting: boolean;
  onSave(label: string, url: string): void;
  onRemove(): void;
  onClose(): void;
}

export const ComposerLinkModal = ({
  initialLabel,
  initialUrl,
  isExisting,
  onSave,
  onRemove,
  onClose
}: ComposerLinkModalProps) => {
  const [label, setLabel] = useState(initialLabel);
  const [url, setUrl] = useState(initialUrl);
  const [error, setError] = useState<string | null>(null);
  const urlRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    urlRef.current?.focus();
    urlRef.current?.select();
  }, []);

  useEffect(() => {
    // Capture: Esc закрывает только это окно, а не чат/ответ под ним.
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.stopPropagation();
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  const submit = (): void => {
    const cleanLabel = cleanLinkLabel(label);
    if (cleanLabel.length === 0) {
      setError("Текст ссылки не может быть пустым.");
      return;
    }
    const normalized = normalizeLinkUrl(url);
    if (!normalized) {
      setError("Введите адрес вида https://example.com");
      return;
    }
    onSave(cleanLabel, normalized);
  };

  return createPortal(
    <div className="folder-editor" role="dialog" aria-modal="true" aria-label="Ссылка">
      <button type="button" className="folder-editor__backdrop" onClick={onClose} aria-label="Закрыть" />
      <form
        className="folder-editor__panel composer-link-modal"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className="folder-editor__header">
          <strong>{isExisting ? "Изменить ссылку" : "Добавить ссылку"}</strong>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>
        <label className="folder-editor__row">
          <span>Текст</span>
          <input value={label} onChange={(event) => setLabel(event.target.value)} />
        </label>
        <label className="folder-editor__row">
          <span>Ссылка</span>
          <input
            ref={urlRef}
            value={url}
            onChange={(event) => {
              setUrl(event.target.value);
              setError(null);
            }}
            placeholder="https://"
            inputMode="url"
          />
        </label>
        <div className="folder-editor__hint">
          Получатель увидит только текст — нажатие на него откроет ссылку.
        </div>
        {error ? <div className="side-card__error">{error}</div> : null}
        <div className="folder-editor__actions">
          {isExisting ? (
            <button
              type="button"
              className="button button--ghost chat-selection-bar__danger"
              onClick={onRemove}
            >
              Удалить ссылку
            </button>
          ) : null}
          <button type="button" className="button button--ghost" onClick={onClose}>
            Отмена
          </button>
          <button type="submit" className="button">
            Сохранить
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
};
